import React, { useCallback, useMemo, useRef } from 'react'

import moment from 'moment/moment'
import PropTypes from 'prop-types'

import join from 'lodash/join'
import map from 'lodash/map'
import uniqueId from 'lodash/uniqueId'
import upperFirst from 'lodash/upperFirst'

import {
  Divider,
  List,
  ListItemText
} from '@material-ui/core'

import { moreVertical, people } from '@galo-monitor/icons'
import { colors } from '@galo-monitor/styles'
import Card from '@galo-monitor/web-components/Card'
import Icon from '@galo-monitor/web-components/Icon'
import IconButton from '@galo-monitor/web-components/IconButton'
import Popover from '@galo-monitor/web-components/Popover'

import useStyles from './styles'
import {
  CardFooter,
  CardTitle,
  CardTitleContainer,
  ProjectChip,
  ListItem
} from './theme'

const PROJECT_ACTIONS = ['edit', 'delete']

const ProjectCard = props => {
  const {
    project,
    t,
    userActions,
    onCustomActionClick,
    onDeleteClick,
    onEditClick
  } = props
  const classes = useStyles()
  const multipleActionsPopoverRef = useRef(null)

  const projectStatus = useMemo(() => {
    const today = moment()
    const projectStartDate = moment(project.start)
    const projectEndDate = moment(project.finish)

    if (today.isAfter(projectEndDate))
      return 'finished'

    return today.isAfter(projectStartDate) ? 'started' : 'waiting'
  }, [project.finish, project.start])

  const onActionClick = useCallback(action => {
    // eslint-disable-next-line no-unused-expressions
    multipleActionsPopoverRef?.current?.onClose()
    switch(action) {
      case 'delete':
        onDeleteClick(project)
        break

      case 'edit':
        onEditClick(project)
        break

      default: onCustomActionClick(project)
    }
  }, [onCustomActionClick, onDeleteClick, onEditClick, project])

  return (
    <Card
      cardStyle={ {
        padding: '15px 25px',
        opacity: project.isActive ? 1 : 0.65
      } }
      cardContentProps={
        {
          style: {
            display: 'flex',
            flexDirection: 'column',
            gap: 15,
            justifyContent: 'space-between'
          }
        }
      }
      title={
        <CardTitleContainer>
          <CardTitle>{project.name}</CardTitle>
          <div>
            <Popover
              ref={ multipleActionsPopoverRef }
              popoverId={ uniqueId('popover-actions') }
              Component={ popoverProps => (
                <IconButton
                  className={ classes.noPadding }
                  tooltip={ t('action', { howMany: 2 }) }
                  size="small"
                  { ...popoverProps }
                >
                  <Icon icon={ moreVertical } color={ colors.darkGrey } />
                </IconButton>
              ) }
            >
              <List style={ { padding: 0 } }>
                {project.isActive ? (
                  map(PROJECT_ACTIONS, action => userActions[action] ? (
                    <React.Fragment key={ uniqueId(action) }>
                      <ListItem
                        className={ join([
                          classes.item,
                          classes.actionItem
                        ], ' ') }
                        color={ action === 'delete' ? colors.red : colors.black }
                        onClick={ () => onActionClick(action) }
                        button
                      >
                        <ListItemText primary={ upperFirst(t(action === 'delete' ? 'archive' : action)) } />
                      </ListItem>
                      <Divider style={ { margin: 0 } } />
                    </React.Fragment>
                  ) : null)
                ) : (
                  <ListItem
                    className={ join([
                      classes.item,
                      classes.actionItem
                    ], ' ') }
                    color={ colors.black }
                    onClick={ onActionClick }
                    button
                  >
                    <ListItemText primary={ upperFirst(t('recover')) } />
                  </ListItem>
                )}
              </List>
            </Popover>
          </div>
        </CardTitleContainer>
      }
      noWrapper
    >
      <ProjectChip
        label={ t('project status', { status: projectStatus }) }
        status={ projectStatus }
      />
      <CardFooter>
        <Icon icon={ people } color={ colors.lightGrey2 } />
        {project.users.length}
      </CardFooter>
    </Card>
  )
}

ProjectCard.propTypes = {
  project: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  onCustomActionClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onEditClick: PropTypes.func,
  userActions: PropTypes.object.isRequired
}

ProjectCard.defaultProps = {
  onCustomActionClick: () => {},
  onDeleteClick: () => {},
  onEditClick: () => {}
}

export default ProjectCard